import { useState, type FormEvent, type ReactNode } from "react";
import type { AuditFile } from "./india";
import {
  newQuery,
  issueQuery,
  addQueryMessage,
  changeQueryStatus,
  reopenQuery,
  escalateQuery,
  exportQueriesCsv,
  querySummary,
  isQueryOverdue,
  type AuditQuery,
  type QueryStatus,
  type QueryPriority,
} from "./queries";

const statusLabels: Record<QueryStatus, string> = {
  draft: "Draft",
  issued: "Awaiting response",
  "partially-answered": "Partially answered",
  "ready-for-review": "Ready for review",
  closed: "Closed",
};
function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="afi-field">
      <span>{label}</span>
      {children}
    </label>
  );
}
function exportSheet(queries: AuditQuery[], clientFacing: boolean) {
  const blob = new Blob([exportQueriesCsv(queries, { clientFacing })], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = clientFacing
    ? "client-query-sheet.csv"
    : "internal-query-register.csv";
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function QueryManagement({
  file,
  actor,
  onSave,
}: {
  file: AuditFile;
  actor: string;
  onSave: (file: AuditFile, action: string) => void;
}) {
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [error, setError] = useState("");
  const summary = querySummary(file.queries);
  const current = file.queries.find((q) => q.id === selected);
  const rows = file.queries.filter((q) => {
    const match =
      `${q.reference} ${q.title} ${q.area} ${q.owner} ${q.clientContact}`
        .toLowerCase()
        .includes(search.toLowerCase());
    return (
      match &&
      (filter === "all" ||
        (filter === "overdue"
          ? isQueryOverdue(q)
          : filter === "blocking"
            ? q.blocksCompletion && q.status !== "closed"
            : q.status === filter))
    );
  });
  function attempt(work: () => void) {
    try {
      work();
      setError("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unable to save query.");
    }
  }
  function update(query: AuditQuery, action: string) {
    onSave(
      {
        ...file,
        queries: file.queries.map((q) => (q.id === query.id ? query : q)),
      },
      `${query.reference}: ${action}`,
    );
  }
  return (
    <div className="afi-query-module">
      <div className="afi-toolbar">
        <div>
          <h2>Query management</h2>
          <p>Track audit questions from request to reviewed conclusion.</p>
        </div>
        <button
          className="afi-primary"
          onClick={() => {
            setCreating(true);
            setSelected("");
          }}
        >
          + Raise query
        </button>
      </div>
      <p className="afi-query-note">
        Evaluation workspace: issuing records a local status only. No messages
        or reminders are sent. Client thread content is intended for sharing;
        internal notes stay out of the client query sheet.
      </p>
      <div className="afi-stats small">
        <div>
          <span>Open queries</span>
          <strong>{summary.open}</strong>
        </div>
        <div>
          <span>Overdue</span>
          <strong>{summary.overdue}</strong>
        </div>
        <div>
          <span>Ready for review</span>
          <strong>{summary.readyForReview}</strong>
        </div>
        <div>
          <span>Completion blockers</span>
          <strong>{summary.blocking}</strong>
        </div>
      </div>
      {error && (
        <div className="afi-error" role="alert">
          {error}
        </div>
      )}
      {creating && (
        <NewQuery
          file={file}
          onCancel={() => setCreating(false)}
          onCreate={(input) =>
            attempt(() => {
              const q = newQuery(input, actor, file.queries.length);
              onSave(
                { ...file, queries: [...file.queries, q] },
                `${q.reference}: raised query`,
              );
              setCreating(false);
              setSelected(q.id);
            })
          }
        />
      )}
      <section className="afi-panel">
        <header>
          <h3>Query register</h3>
          <div className="afi-actions">
            <button
              disabled={!file.queries.some((q) => q.status !== "draft")}
              onClick={() => attempt(() => exportSheet(file.queries, true))}
            >
              Export client sheet
            </button>
            <button
              disabled={!file.queries.length}
              onClick={() => attempt(() => exportSheet(file.queries, false))}
            >
              Export internal register
            </button>
          </div>
        </header>
        <div className="afi-grid">
          <Field label="Search queries">
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Reference, title, owner or area"
            />
          </Field>
          <Field label="Query filter">
            <select value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="all">All queries</option>
              <option value="overdue">Overdue</option>
              <option value="blocking">Completion blockers</option>
              {Object.entries(statusLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="afi-table-wrap">
          <table>
            <thead>
              <tr>
                <th>Query / area</th>
                <th>Owner / contact</th>
                <th>Priority</th>
                <th>Due date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((q) => (
                <tr key={q.id}>
                  <td>
                    <button
                      className="afi-link"
                      aria-expanded={selected === q.id}
                      onClick={() => {
                        setSelected(q.id);
                        setCreating(false);
                        setError("");
                      }}
                    >
                      {q.reference} · {q.title}
                    </button>
                    <small>
                      {q.area}
                      {q.blocksCompletion ? " · Blocks completion" : ""}
                    </small>
                  </td>
                  <td>
                    {q.owner}
                    <small>{q.clientContact}</small>
                  </td>
                  <td>
                    <span
                      className={`afi-tag ${q.priority === "Critical" || q.priority === "High" ? "warning" : "neutral"}`}
                    >
                      {q.priority}
                    </span>
                  </td>
                  <td>
                    {q.dueDate}
                    {isQueryOverdue(q) && (
                      <small className="afi-query-overdue">Overdue</small>
                    )}
                  </td>
                  <td>
                    {statusLabels[q.status]}
                    {q.escalationReason && <small>Escalation recorded</small>}
                  </td>
                </tr>
              ))}
              {!rows.length && (
                <tr>
                  <td colSpan={5}>
                    <p>
                      {file.queries.length
                        ? "No queries match these filters."
                        : "No queries yet. Raise a question and link it to its audit procedure."}
                    </p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <small>
          Exports include the full register, regardless of filters. Client
          sheets exclude drafts, internal notes, evidence references and closure
          conclusions. Review client-thread text before sharing.
        </small>
      </section>
      {current && (
        <QueryDetail
          key={current.id}
          query={current}
          file={file}
          actor={actor}
          onUpdate={update}
          attempt={attempt}
        />
      )}
    </div>
  );
}

function NewQuery({
  file,
  onCreate,
  onCancel,
}: {
  file: AuditFile;
  onCreate: (input: Parameters<typeof newQuery>[0]) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [area, setArea] = useState("Revenue");
  const [owner, setOwner] = useState("");
  const [contact, setContact] = useState("");
  const [due, setDue] = useState("");
  const [priority, setPriority] = useState<QueryPriority>("Normal");
  const [procedure, setProcedure] = useState("");
  const [impact, setImpact] = useState("");
  const [blocks, setBlocks] = useState(true);
  function submit(e: FormEvent) {
    e.preventDefault();
    onCreate({
      title,
      description,
      area,
      owner,
      clientContact: contact,
      dueDate: due,
      priority,
      procedureId: procedure || undefined,
      financialImpact: Number(impact || 0),
      blocksCompletion: blocks,
    });
  }
  return (
    <form className="afi-panel" onSubmit={submit}>
      <header>
        <h3>Raise audit query</h3>
      </header>
      <div className="afi-grid">
        <Field label="Query title (client-facing)">
          <input
            required
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </Field>
        <Field label="Audit area">
          <input
            required
            value={area}
            onChange={(e) => setArea(e.target.value)}
          />
        </Field>
        <Field label="Audit owner">
          <input
            required
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
          />
        </Field>
        <Field label="Client contact">
          <input
            required
            value={contact}
            onChange={(e) => setContact(e.target.value)}
          />
        </Field>
        <Field label="Response due date">
          <input
            required
            type="date"
            value={due}
            onChange={(e) => setDue(e.target.value)}
          />
        </Field>
        <Field label="Query priority">
          <select
            value={priority}
            onChange={(e) => setPriority(e.target.value as QueryPriority)}
          >
            {["Low", "Normal", "High", "Critical"].map((p) => (
              <option key={p}>{p}</option>
            ))}
          </select>
        </Field>
        <Field label="Linked procedure">
          <select
            value={procedure}
            onChange={(e) => setProcedure(e.target.value)}
          >
            <option value="">Unlinked</option>
            {file.procedures.map((p) => (
              <option key={p.id} value={p.id}>
                {p.id} · {p.title}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Potential financial impact (INR, internal)">
          <input
            type="number"
            min="0"
            step="0.01"
            value={impact}
            onChange={(e) => setImpact(e.target.value)}
          />
        </Field>
      </div>
      <Field label="Question and requested evidence (client-facing)">
        <textarea
          required
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Field>
      <label className="afi-check">
        <input
          type="checkbox"
          checked={blocks}
          onChange={(e) => setBlocks(e.target.checked)}
        />
        Must close before engagement completion
      </label>
      <div className="afi-actions">
        <button type="button" onClick={onCancel}>
          Cancel query
        </button>
        <button className="afi-primary" type="submit">
          Save query draft
        </button>
      </div>
    </form>
  );
}

function QueryDetail({
  query: q,
  file,
  actor,
  onUpdate,
  attempt,
}: {
  query: AuditQuery;
  file: AuditFile;
  actor: string;
  onUpdate: (q: AuditQuery, action: string) => void;
  attempt: (work: () => void) => void;
}) {
  const [text, setText] = useState("");
  const [evidence, setEvidence] = useState("");
  const [visibility, setVisibility] = useState<"internal" | "client">(
    "internal",
  );
  const [reason, setReason] = useState("");
  const [escalation, setEscalation] = useState("");
  const [returnReason, setReturnReason] = useState("");
  const closed = q.status === "closed";
  const procedure = file.procedures.find((p) => p.id === q.procedureId);
  const linkedUnavailable = !!q.procedureId && !procedure;
  return (
    <section className="afi-panel afi-query-detail" aria-label="Selected query">
      <header>
        <div>
          <small>
            {q.reference} · {q.area}
          </small>
          <h3>{q.title}</h3>
        </div>
        <span className="afi-tag">{statusLabels[q.status]}</span>
      </header>
      <p className="afi-query-text">{q.description}</p>
      <p>
        <strong>Owner:</strong> {q.owner} · <strong>Client contact:</strong>{" "}
        {q.clientContact} · <strong>Due:</strong> {q.dueDate}
      </p>
      <p>
        Linked procedure:{" "}
        {procedure
          ? `${procedure.id} · ${procedure.title}`
          : linkedUnavailable
            ? `${q.procedureId} (no longer in programme)`
            : "None"}
        . Potential financial impact: ₹
        {q.financialImpact.toLocaleString("en-IN")} (internal).
      </p>
      {linkedUnavailable && (
        <p className="afi-query-overdue">
          The linked procedure was removed from the programme. Review that
          change and explain its effect in the closure conclusion before closing
          this query.
        </p>
      )}
      <p className="afi-muted">
        Closing a query records its resolution. The linked procedure still needs
        its own evidence, conclusion and review.
      </p>
      {q.escalationReason && (
        <div className="afi-query-note">
          <strong>Latest escalation (internal)</strong>
          <p className="afi-query-text">{q.escalationReason}</p>
        </div>
      )}
      {q.status === "draft" && (
        <button
          className="afi-primary"
          onClick={() =>
            attempt(() => onUpdate(issueQuery(q, actor), "issued locally"))
          }
        >
          Issue query locally
        </button>
      )}
      <h3>Discussion and history</h3>
      <div className="afi-query-messages">
        {q.messages.map((m) => (
          <article key={m.id} className={`afi-query-message ${m.visibility}`}>
            <header>
              <strong>
                {m.visibility === "internal"
                  ? "Internal only"
                  : "Client thread"}
              </strong>
              <small>
                {m.author} · {new Date(m.at).toLocaleString("en-IN")}
              </small>
            </header>
            <p className="afi-query-text">{m.text}</p>
            {m.evidenceRef && (
              <small>Evidence reference (internal): {m.evidenceRef}</small>
            )}
          </article>
        ))}
        {!q.messages.length && <p>No discussion recorded.</p>}
      </div>
      {!closed && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            attempt(() => {
              onUpdate(
                addQueryMessage(
                  q,
                  { text, evidenceRef: evidence, visibility },
                  actor,
                ),
                visibility === "client"
                  ? "recorded client-thread response"
                  : "added internal note",
              );
              setText("");
              setEvidence("");
            });
          }}
        >
          <div className="afi-grid">
            <Field label="Message visibility">
              <select
                value={visibility}
                onChange={(e) =>
                  setVisibility(e.target.value as "internal" | "client")
                }
              >
                <option value="internal">Internal note</option>
                <option value="client">Client thread (shareable)</option>
              </select>
            </Field>
            <Field label="Query evidence reference (internal)">
              <input
                value={evidence}
                onChange={(e) => setEvidence(e.target.value)}
                placeholder="Reference in the audit evidence file"
              />
            </Field>
          </div>
          <Field
            label={
              visibility === "internal"
                ? "Internal query note"
                : "Client-thread response"
            }
          >
            <textarea
              required
              rows={3}
              value={text}
              onChange={(e) => setText(e.target.value)}
            />
          </Field>
          <button type="submit">Add query message</button>
        </form>
      )}
      {q.status !== "draft" && !closed && (
        <div className="afi-query-transitions">
          <Field label="Reason for further response (internal)">
            <input
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
            />
          </Field>
          <div className="afi-actions">
            <button
              disabled={q.status === "issued"}
              onClick={() =>
                attempt(() => {
                  onUpdate(
                    changeQueryStatus(q, "issued", actor, returnReason),
                    "requested further response",
                  );
                  setReturnReason("");
                })
              }
            >
              Return for further response
            </button>
            <button
              disabled={q.status === "ready-for-review"}
              onClick={() =>
                attempt(() =>
                  onUpdate(
                    changeQueryStatus(q, "ready-for-review", actor),
                    "submitted for review",
                  ),
                )
              }
            >
              Submit query for review
            </button>
          </div>
        </div>
      )}
      {closed && (
        <div className="afi-query-note">
          <strong>Reviewed closure · {q.closedBy}</strong>
          <p className="afi-query-text">{q.closureConclusion}</p>
        </div>
      )}
      {(q.status === "ready-for-review" || closed) && (
        <div className="afi-query-decision">
          <Field
            label={
              closed
                ? "Reason to reopen query"
                : "Query closure conclusion (internal)"
            }
          >
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
            />
          </Field>
          {closed ? (
            <button
              onClick={() =>
                attempt(() => {
                  onUpdate(
                    reopenQuery(q, actor, reason),
                    "reopened with reason",
                  );
                  setReason("");
                })
              }
            >
              Reopen query
            </button>
          ) : (
            <>
              <small>
                Closure requires evidence and a reviewer independent of the
                query creator, client-thread responders and evidence preparers.
              </small>
              <button
                className="afi-primary"
                onClick={() =>
                  attempt(() => {
                    onUpdate(
                      changeQueryStatus(q, "closed", actor, reason),
                      "closed after independent review",
                    );
                    setReason("");
                  })
                }
              >
                Close after review
              </button>
            </>
          )}
        </div>
      )}
      {!closed && (
        <div className="afi-query-decision">
          <Field label="Escalation reason (internal)">
            <textarea
              rows={2}
              value={escalation}
              onChange={(e) => setEscalation(e.target.value)}
            />
          </Field>
          <button
            onClick={() =>
              attempt(() => {
                onUpdate(
                  escalateQuery(q, actor, escalation),
                  "recorded escalation",
                );
                setEscalation("");
              })
            }
          >
            Record escalation
          </button>
          <small>
            Records the reason in the audit history. No notification is sent.
          </small>
        </div>
      )}
    </section>
  );
}
